import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { SiWhatsapp } from "react-icons/si";
import { track } from "@/lib/analytics";
import { CTA_LABEL, WHATSAPP_DEFAULT, capacity, guarantee } from "@/lib/offer";

const EASE = [0.16, 1, 0.3, 1];

// Section anchors ("/#cases") scroll in place on the home page and route back to
// it from other pages; plain paths route client-side.
export function SiteLink({ href, ...props }) {
  const { pathname } = useLocation();
  if (href.startsWith("/#") && pathname === "/") return <a href={href.slice(1)} {...props} />;
  return <Link to={href} {...props} />;
}

export function Logo({ className = "" }) {
  return (
    <SiteLink
      href="/#hero"
      aria-label="Ambitie, voltar ao início"
      className={`inline-flex items-baseline py-1.5 text-2xl font-bold tracking-[-0.04em] text-primary ${className}`}
    >
      Ambitie<span className="text-accent">.</span>
    </SiteLink>
  );
}

export function WhatsAppCta({
  href = WHATSAPP_DEFAULT,
  label = CTA_LABEL,
  variant = "primary",
  size = "lg",
  location,
  className = "",
  onClick,
  ...props
}) {
  const sizes = {
    lg: "h-12 px-6 text-base gap-2.5",
    md: "h-11 px-5 text-sm gap-2",
    sm: "h-10 px-4 text-sm gap-2",
  };
  const variants = {
    primary: "bg-primary text-primary-foreground hover:bg-[#1A3796] shadow-[0_8px_24px_-12px_rgba(30,64,175,0.6)]",
    outline: "border border-primary/30 text-primary hover:border-primary hover:bg-primary/5",
    inverse: "bg-white text-primary hover:bg-white/90",
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        track("cta_click", { location: location ?? props["data-testid"] ?? "unknown", label });
        onClick?.(event);
      }}
      className={`group inline-flex items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-[background-color,border-color,transform] duration-200 active:translate-y-px ${sizes[size]} ${variants[variant]} ${className}`}
      {...props}
    >
      <SiWhatsapp aria-hidden="true" className="h-4 w-4 shrink-0" />
      {label}
      {size !== "sm" && (
        <ArrowRight
          aria-hidden="true"
          className="-ml-0.5 h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none"
        />
      )}
    </a>
  );
}

// Small line under a CTA that answers the objection the label leaves open.
export function CtaNote({ children, className = "" }) {
  return <p className={`text-sm text-muted-foreground ${className}`}>{children}</p>;
}

// Renders only when `guarantee` is filled in lib/offer.js.
export function GuaranteeNote({ inverse = false, className = "" }) {
  if (!guarantee) return null;
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <ShieldCheck
        aria-hidden="true"
        className={`mt-0.5 h-5 w-5 shrink-0 ${inverse ? "text-accent" : "text-primary"}`}
      />
      <p className={`text-sm leading-relaxed ${inverse ? "text-white/80" : "text-muted-foreground"}`}>
        <span className={`font-semibold ${inverse ? "text-white" : "text-foreground"}`}>
          {guarantee.title}.
        </span>{" "}
        {guarantee.text}
      </p>
    </div>
  );
}

// Renders only when `capacity` is filled in lib/offer.js.
export function CapacityBadge({ inverse = false, className = "" }) {
  if (!capacity) return null;
  return (
    <p
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${
        inverse ? "bg-white/10 text-white" : "bg-accent/15 text-foreground"
      } ${className}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60 motion-reduce:hidden" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
      </span>
      {capacity.open} de {capacity.total} vagas abertas para {capacity.month}
    </p>
  );
}

// Counts the leading number of a stat ("251+", "6+ anos") up from zero the
// first time it scrolls into view. Non-numeric values render as-is.
export function CountUp({ value, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();
  const match = /^(\d+)(.*)$/.exec(value);
  const target = match ? Number(match[1]) : 0;
  const [current, setCurrent] = useState(match && !reduce ? 0 : target);

  useEffect(() => {
    if (!match || reduce || !inView) return undefined;
    const controls = animate(0, target, {
      duration: 1.4,
      ease: EASE,
      onUpdate: (latest) => setCurrent(Math.round(latest)),
    });
    return () => controls.stop();
  }, [inView, reduce, target]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!match) return <span className={className}>{value}</span>;
  return (
    <span ref={ref} className={`tabular-nums ${className}`}>
      <span aria-hidden="true">
        {current}
        {match[2]}
      </span>
      <span className="sr-only">{value}</span>
    </span>
  );
}

// Fades a block in once when it enters the viewport. MotionConfig in App
// turns this into a no-op for visitors who prefer reduced motion.
export function Reveal({ children, delay = 0, className = "", as = "div" }) {
  const Component = motion[as];
  return (
    <Component
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.6, delay, ease: EASE }}
      className={className}
    >
      {children}
    </Component>
  );
}

export const easing = EASE;
